import { LanguageContext } from '@axon-enterprise/i18n-helpers';
import {
  Box,
  LinearProgress,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect } from 'react';

import { mixpanelService } from '../common/tracking/MixpanelService';
import { getAppTitle, AXON_LOGO_PATH, DEFAULT_REDIRECT_DELAY_MS, TRACKING_SESSION_ID } from '../utils/constants';
import { appendQueryParam } from '../utils/url';

interface RedirectPageProps {
  serial: string;
  redirectUrl: string;
  onRedirect?: () => void;
  redirectDelay?: number;
}

export const RedirectPage: React.FC<RedirectPageProps> = ({
  serial,
  redirectUrl,
  onRedirect,
  redirectDelay = DEFAULT_REDIRECT_DELAY_MS
}) => {
  const { language } = useContext(LanguageContext);
  
  const redirectWithSession = React.useMemo(() => {
    const id = mixpanelService.getSessionId();
    let url = appendQueryParam(redirectUrl, TRACKING_SESSION_ID, id || undefined);
    // Append language parameter to pass to the core website
    url = appendQueryParam(url, 'language', language);
    return url;
  }, [redirectUrl, language]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (onRedirect) {
        onRedirect();
      } else {
        window.location.href = redirectWithSession;
      }
    }, redirectDelay);

    return () => clearTimeout(timer);
  }, [redirectWithSession, onRedirect, redirectDelay]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#e5e5e5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      {/* Centered white card */}
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          p: 4,
          textAlign: 'center',
          maxWidth: 400,
          width: '100%'
        }}
      >
        {/* Axon Logo */}
        <Box
          component="img"
          src={AXON_LOGO_PATH}
          alt="Axon Logo"
          sx={{
            height: 40,
            width: 'auto',
            mb: 3
          }}
        />

        {/* Title */}
        <Typography 
          variant="h6"
          sx={{ 
            fontWeight: 600,
            mb: 2,
            color: '#000'
          }}
        >
          {getAppTitle()}
        </Typography>
        
        {/* Subtitle */}
        <Typography 
          variant="body2"
          sx={{ 
            mb: 1.5,
            color: '#333',
            lineHeight: 1.4
          }}
        >
          {__('Redirecting to your device configuration page.')}
        </Typography>

        {/* URL */}
        <Typography 
          variant="caption"
          sx={{ 
            display: 'block',
            mb: 2,
            color: '#666',
            fontSize: '0.75rem'
          }}
        >
          {redirectWithSession}
        </Typography>

        {/* Manual redirect text */}
        <Typography 
          variant="caption"
          sx={{ 
            display: 'block',
            mb: 3,
            color: '#333',
            fontSize: '0.75rem'
          }}
        >
          {__('If you aren\'t redirected automatically, $[link].', {
            link: (
              <Box 
                component="span" 
                sx={{ 
                  color: '#1976d2',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
                onClick={() => window.location.href = redirectWithSession}
                key="redirect-link"
              >
                {__('click here')}
              </Box>
            )
          })}
        </Typography>

        {/* Progress bar */}
        <Box sx={{ mb: 3 }}>
          <LinearProgress 
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main',
                borderRadius: 4
              }
            }}
          />
        </Box>

        {/* Serial Number */}
        <Typography 
          variant="caption"
          sx={{ 
            fontSize: '0.75rem',
            color: '#666'
          }}
        >
          {__('SN $[serial]', { serial })}
        </Typography>
      </Box>
    </Box>
  );
};

export default RedirectPage; 